import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Input, Button, Card, Divider, Row, Col, notification, Select } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { API_BASE } from './api/client';
import './JobForm.css';

const { TextArea } = Input;
const { Option } = Select;

const JobForm = ({ onJobCreated}) => {
    const [form] = Form.useForm();
    const [fields, setFields] = useState([
        { label: 'Job Title', name: 'title', type: 'text', required: true },
        { label: 'Job Description', name: 'description', type: 'textarea', required: true },
        { label: 'Requirements', name: 'requirements', type: 'textarea' },
        { label: 'Location', name: 'location', type: 'text' },
        { label: 'Salary', name: 'salary', type: 'text' },
    ]);

    const [applicationForm] = Form.useForm();
    const [formElements, setFormElements] = useState([
        { label: 'Full Name', name: 'name', type: 'text', required: true },
        { label: 'Email Address', name: 'email', type: 'email', required: true },
        { label: 'Phone Number', name: 'phone', type: 'text', required: true },
        { label: 'Address', name: 'address', type: 'textarea', required: false },
        { label: 'LinkedIn Profile', name: 'linkedin', type: 'text', required: false },
        { label: 'Portfolio URL', name: 'portfolio', type: 'text', required: false },
        { label: 'Resume', name: 'resume', type: 'file', required: true },
        { label: 'Cover Letter', name: 'coverLetter', type: 'file', required: false },
        { label: 'Skills', name: 'skills', type: 'textarea', required: false },
        { label: 'Employment Status', name: 'employmentStatus', type: 'select', options: [{ label: 'Employed', value: 'employed' }, { label: 'Unemployed', value: 'unemployed' }], required: true },
        { label: 'Years of Experience', name: 'experience', type: 'number', required: true },
        { label: 'Availability to Start', name: 'startDate', type: 'date', required: true },
        { label: 'References', name: 'references', type: 'textarea', required: false },
        { label: 'Expected Salary', name: 'expectedSalary', type: 'number', required: false },
        { label: 'Willingness to Relocate', name: 'relocate', type: 'select', options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }], required: true },
        { label: 'How Did You Hear About Us?', name: 'referralSource', type: 'text', required: false },
        { label: 'Professional Certifications', name: 'certifications', type: 'textarea', required: false },
        { label: 'Languages Spoken', name: 'languages', type: 'textarea', required: false },
        { label: 'Availability for Interview', name: 'interviewAvailability', type: 'textarea', required: false },
        { label: 'Additional Information', name: 'additionalInfo', type: 'textarea', required: false },
    ]);

    const [customStyles, setCustomStyles] = useState({ color: 'black' });
    const [formValues, setFormValues] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false); // Flag to prevent multiple submissions


    useEffect(() => {
        const savedDraft = localStorage.getItem('jobDraft');
        if (savedDraft) {
            const draftValues = JSON.parse(savedDraft);
            form.setFieldsValue(draftValues);
        }
    }, [form]);

    const handleFinish = async () => {
        if (isSubmitting) return;
    
        setIsSubmitting(true);
        try {
            await applicationForm.submit();
    
            // Get updated jobData using form.getFieldsValue()
            const jobData = form.getFieldsValue(true); // Updated here
    
            const fieldsPayload = fields.map((field) => ({
                label: field.label,
                value: jobData[field.name] || "",
            }));
    
            const formElementsPayload = formElements.map((formElement) => ({
                label: formElement.label,
                name: formElement.name,
                type: formElement.type,
                ...(formElement.type === "select" && { options: formElement.options }),
            }));
    
            const payload = {
                fields: fieldsPayload,
                formElements: formElementsPayload,
            };
    
            const token = localStorage.getItem("token");
            await axios.post(
                `${API_BASE}/api/job/postjobs`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
    
            onJobCreated(payload);
            form.resetFields();
            applicationForm.resetFields();
            localStorage.removeItem("jobDraft");
    
            notification.success({
                message: "Job listing and application form submitted successfully!",
            });
        } catch (error) {
            console.error("Error submitting form:", error);
            notification.error({ message: "Failed to submit job and application form." });
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const addField = () => {
        setFields([...fields, { label: 'New Field', name: `field_${fields.length}`, type: 'text', required: false }]);
    };

    const removeField = (name) => {
        setFields(fields.filter(field => field.name !== name));
    };

    const handleLabelChange = (e, index) => {
        const updatedFields = [...fields];
        updatedFields[index].label = e.target.value;
        setFields(updatedFields);
    };

    const handleTypeChange = (value, index) => {
        const updatedFields = [...fields];
        updatedFields[index].type = value;
        setFields(updatedFields);
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const reorderedFields = Array.from(fields);
        const [removed] = reorderedFields.splice(result.source.index, 1);
        reorderedFields.splice(result.destination.index, 0, removed);
        setFields(reorderedFields);
    };

    const generateRules = (field) => {
        const rules = [];
        if (field.required) rules.push({ required: true, message: `${field.label} is required` });
        if (field.type === 'email') rules.push({ type: 'email', message: 'Please enter a valid email!' });
        if (field.type === 'number') rules.push({ pattern: /^[0-9]*$/, message: 'Please enter a valid number!' });
        return rules;
    };

    const renderField = (field) => {
    const props = { placeholder: field.label, value: formValues[field.name] || "" };
    switch (field.type) {
        case 'text':
            return <Input {...props} />;
        case 'textarea':
            return <TextArea rows={3} {...props} />;
        default:
            return <Input {...props} />;
    }
};

    const saveDraft = () => {
        const values = form.getFieldsValue();
        localStorage.setItem('jobDraft', JSON.stringify(values));
        form.setFieldsValue(values);
        setFormValues(values);
        notification.success({ message: 'Draft saved successfully!' });
    };
    

    const renderPreview = () => {
        return (
            <div>
                <h3>Preview</h3>
                {fields.map(field => (
                    <div key={field.name}>
                        <strong>{field.label}:</strong> {formValues[field.name] || "Not entered yet"}
                    </div>
                ))}
            </div>
        );
    };


    useEffect(() => {
        const savedDraft = localStorage.getItem('jobDraft');
        if (savedDraft) {
            const draftValues = JSON.parse(savedDraft); // Parse saved draft data
            form.setFieldsValue(draftValues); // Populate form fields with saved values
            setFormValues(draftValues); // Update formValues state for live preview
        }
    }, [form]);

    const handleAddFormElement = () => {
        const newFormElementLabel = 'New Field';
        const newFormElementName = newFormElementLabel.replace(/\s+/g, '_').toLowerCase();
        setFormElements([...formElements, { label: newFormElementLabel, name: newFormElementName, type: 'text', required: false, options: [] }]);
    };

    const handleRemoveFormElement = (index) => {
        const updatedFormElements = [...formElements];
        updatedFormElements.splice(index, 1);
        setFormElements(updatedFormElements);
    };

    const handleFormElementChange = (index, key, value) => {
        const updatedFormElements = [...formElements];
        if (key === 'label') {
            updatedFormElements[index].label = value;
            updatedFormElements[index].name = value.replace(/\s+/g, '_').toLowerCase();
        } else if (key === 'name') {
            updatedFormElements[index].name = value.toLowerCase();
        } else {
            updatedFormElements[index][key] = value;
        }
        if (key === 'type' && value !== 'select') {
            updatedFormElements[index].options = [];
        }
        setFormElements(updatedFormElements);
    };

    const handleOptionLabelChange = (formElementIndex, optionIndex, value) => {
        const updatedFormElements = [...formElements];
        updatedFormElements[formElementIndex].options[optionIndex].label = value;
        setFormElements(updatedFormElements);
    };

    const handleOptionValueChange = (formElementIndex, optionIndex, value) => {
        const updatedFormElements = [...formElements];
        updatedFormElements[formElementIndex].options[optionIndex].value = value;
        setFormElements(updatedFormElements);
    };

    const addOption = (formElementIndex) => {
        const updatedFormElements = [...formElements];
        updatedFormElements[formElementIndex].options = [...(updatedFormElements[formElementIndex].options || []), { label: '', value: '' }];
        setFormElements(updatedFormElements);
    };

    const removeOption = (formElementIndex, optionIndex) => {
        const updatedFormElements = [...formElements];
        updatedFormElements[formElementIndex].options.splice(optionIndex, 1);
        setFormElements(updatedFormElements);
    };

    const moveFormElement = (index, direction) => {
        const updatedFormElements = [...formElements];
        const [movedFormElement] = updatedFormElements.splice(index, 1);
        updatedFormElements.splice(index + direction, 0, movedFormElement);
        setFormElements(updatedFormElements);
    };

    
    const renderFormElementInput = (formElement, index) => (
        <Row key={index} gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
                <Input
                    placeholder="Label"
                    value={formElement.label}
                    onChange={(e) => handleFormElementChange(index, 'label', e.target.value)}
                    style={{ fontWeight: '750' }}
                />
            </Col>
            <Col span={8}>
                <Select
                    value={formElement.type}
                    onChange={(value) => handleFormElementChange(index, 'type', value)}
                >
                    <Option value="text">Text</Option>
                    <Option value="textarea">Textarea</Option>
                    <Option value="email">Email</Option>
                    <Option value="number">Number</Option>
                    <Option value="date">Date</Option>
                    <Option value="file">File</Option>
                    <Option value="select">Select</Option>
                </Select>
            </Col>
            <Col span={4}>
                <Button
                    icon={<i className="fas fa-arrow-up" />}
                    disabled={index === 0}
                    onClick={() => moveFormElement(index, -1)}
                />
                <Button
                    icon={<i className="fas fa-arrow-down" />}
                    disabled={index === formElements.length - 1}
                    onClick={() => moveFormElement(index, 1)}
                    style={{ marginLeft: 4 }}
                />
            </Col>
            <Col span={4}>
                <Button
                    icon={<i className="fas fa-trash"></i>}
                    onClick={() => handleRemoveFormElement(index)}
                    danger
                />
            </Col>
            {formElement.type === 'select' && (
                <Col span={24}>
                    <Divider>Options for {formElement.label}</Divider>
                    {formElement.options?.map((option, optIndex) => (
                        <Row key={optIndex} gutter={16} style={{ marginBottom: 8 }}>
                            <Col span={10}>
                                <Input
                                    placeholder="Option Label"
                                    value={option.label}
                                    onChange={(e) => handleOptionLabelChange(index, optIndex, e.target.value)}
                                />
                            </Col>
                            <Col span={10}>
                                <Input
                                    placeholder="Option Value"
                                    value={option.value}
                                    onChange={(e) => handleOptionValueChange(index, optIndex, e.target.value)}
                                />
                            </Col>
                            <Col span={4}>
                                <Button
                                    icon={<i className="fas fa-trash"></i>}
                                    onClick={() => removeOption(index, optIndex)}
                                    danger
                                />
                            </Col>
                        </Row>
                    ))}
                    <Button
                        type="dashed"
                        onClick={() => addOption(index)}
                        block
                        icon={<i className="fas fa-plus" />}
                        className='add-button'
                    >
                        Add Option
                    </Button>
                </Col>
            )}
        </Row>
    );
    return (
        <Card className="card">
            <h2 className='admin-sub-header'>Create a New Job</h2>
            {/* <Select onChange={(value) => setCustomStyles({ ...customStyles, color: value })}>
                <Option value="red">Red</Option>
                <Option value="blue">Blue</Option>
            </Select> */}
            <Divider />
            <Form
                form={form}
                layout="vertical"
                onFinish={(jobData) => handleFinish(jobData)}
                onValuesChange={(changedValues, allValues) => setFormValues(allValues)}
            >
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                {fields.map((field, index) => (
                                    <Draggable key={field.name} draggableId={field.name} index={index}>
                                        {(provided) => (
                                            <Form.Item
                                                key={field.name}
                                                name={field.name}
                                                rules={generateRules(field)}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className="form-item"
                                            >
                                                <Row align="middle">
                                                    <Col span={6}>
                                                        <Input
                                                            value={field.label}
                                                            onChange={(e) => handleLabelChange(e, index)}
                                                            placeholder="Field Label"
                                                            className="field-label"
                                                            style={{ color: customStyles.color, fontWeight: '750' }}
                                                        />
                                                    </Col>
                                                    <Col span={6}>
                                                        <Select
                                                            defaultValue={field.type}
                                                            onChange={(value) => handleTypeChange(value, index)}
                                                        >
                                                            <Option value="text">Text</Option>
                                                            <Option value="textarea">Textarea</Option>
                                                            <Option value="number">Number</Option>
                                                            <Option value="email">Email</Option>
                                                        </Select>
                                                    </Col>
                                                    <Col span={10}>{renderField(field)}</Col>
                                                    <Col span={2}>
                                                        {fields.length > 1 && (
                                                            <Button
                                                            icon={<i className="fas fa-trash remove-icon" />}
                                                            onClick={() => removeField(field.name)}
                                                            danger
                                                        />
                                                        )}
                                                    </Col>
                                                </Row>
                                            </Form.Item>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                <Form.Item>
                    <Button
                        type="dashed"
                        onClick={addField}
                        block
                        icon={<i className="fas fa-plus" />}
                        className="add-button"
                        disabled={isSubmitting}
                    >
                        Add Another Field
                    </Button>
                </Form.Item>
                {/* <Form.Item>
                    <Button onClick={saveDraft}>Save as Draft</Button>
                </Form.Item> */}
                <Divider />
            </Form>

            <Divider />
           {/*  {renderPreview()} */}
            <h2 className='admin-sub-header'>Create a New Job Application Form</h2>
            <Divider />
            <Form form={applicationForm}  layout="vertical">
                {formElements.map((formElement, index) => renderFormElementInput(formElement, index))}
                <Form.Item>
                    <Button
                        type="dashed"
                        onClick={handleAddFormElement}
                        block
                        icon={<i className="fas fa-plus" />}
                        className='add-button'
                    >
                        Add New Field
                    </Button>
                </Form.Item>
                <Form.Item style={{ textAlign: 'center' }}>
                    <Button onClick={handleFinish} className='admin-submit'><i className="fas fa-paper-plane"></i>
                        Submit
                    </Button>
                </Form.Item>
            </Form>
        </Card>
        
    );
};

export default JobForm;

